import { useState } from "preact/hooks";
import {
  plural,
  storePhotos,
  styled,
  takenNames,
  toucanPill,
  ToucanButton,
  ToucanFilePicker,
  ToucanNewThingDialog,
  ToucanPhotoRow,
  usePhotoDrop,
} from "toucan";
import {
  addChosen,
  addRowPhotos,
  atLeast,
  findLink,
  findPhotos,
  findingStopped,
  parsePaste,
  rowsFor,
  setRowNote,
  setRowPhotos,
  stopFinding,
  toggleRow,
} from "../import.ts";
import type { ImportRow } from "../import.ts";
import { addTag, sortedTags, tagLabel } from "../model.ts";
import { go, listPath } from "../routes.ts";
import { store } from "../state.ts";

const Page = styled("section")`
  min-height: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* The warm canvas every routed page sits on. */
  background: var(--toucan-canvas);

  .note {
    margin: 0;
    color: var(--toucan-muted);
  }

  textarea {
    margin: 0;
  }

  /* Even inside the page's column, a pill takes its label's width: one
     stretched to the column reads as a banner, with its words stranded in the
     middle. */
  & > button {
    align-self: flex-start;
  }

  /* The threshold and what it lets through, on one line: the count is the
     whole point of moving the slider, so it never scrolls away from it. */
  .threshold {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    margin: 0;
  }

  .threshold input {
    flex: 1 1 12rem;
    margin: 0;
  }

  .count {
    margin: 0;
    color: var(--toucan-ink);
  }

  /* What the run is doing, and the way out of it: a lookup is minutes long,
     so Stop sits on the line reporting it rather than elsewhere. */
  .status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
  }

  .left {
    color: var(--toucan-muted);
  }

  .rows,
  .missing {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .rows li::marker,
  .missing li::marker {
    content: none;
  }

  /* One person: the switch and who they are on the first line, the photos and
     where to look for one on the second. */
  .row {
    display: flex;
    flex-direction: column;
    /* Said out loud: a list item here inherits centring, which would take the
       switch, the name and the photos off the left edge and float each of
       them in the middle of its own card. */
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    border: 1px solid var(--toucan-border-strong);
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface);
    box-shadow: var(--toucan-shadow-sm);
  }

  /* A list of rows has no field to focus, so the row last tapped is the one a
     paste lands on. Marked the way a chosen thing is marked everywhere else,
     rather than with a ring, which on this page means the keyboard is here. */
  .row.active {
    border-color: var(--pico-primary);
    background: var(--toucan-accent-soft);
  }

  .who {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Pico gives every input the width of its container, which for a checkbox on
     a full-width row is a switch several inches across. */
  .who input[type="checkbox"] {
    flex: none;
    width: 1.25rem;
    margin: 0;
  }

  .about {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .about small {
    color: var(--toucan-muted);
  }

  /* Where to look by hand, which leaves the app, so a link in the house pill
     rather than a button. */
  .find {
    text-decoration: none;
  }

  .note-field {
    margin: 0;
  }

  .off {
    color: var(--toucan-muted);
  }
`;

/** What one row's second line says about them, once there is anything to say. */
function about(row: ImportRow): string {
  return [
    row.line.email,
    row.line.events === undefined
      ? undefined
      : `came to ${plural(row.line.events, "event")}`,
    row.duplicate === "" ? undefined : row.duplicate,
    row.found === "" ? undefined : row.found,
  ]
    .filter((part) => part !== undefined)
    .join(" · ");
}

/**
 * One person in the review. It shows whether they are coming in and what is
 * known about them, with the photos they will arrive with and the tab to go
 * looking in.
 *
 * Tapping a row makes a paste go to it, so the switch is a control of its own
 * rather than the row itself. Otherwise the two gestures would fight over the
 * same tap.
 */
function ReviewRow({
  row,
  index,
  active,
  onActive,
  onToggle,
  onPhotos,
  onNote,
  onFiles,
}: {
  row: ImportRow;
  index: number;
  active: boolean;
  onActive: (index: number) => void;
  onToggle: (index: number) => void;
  onPhotos: (index: number, photos: string[]) => void;
  onNote: (index: number, note: string) => void;
  onFiles: (index: number, files: Iterable<File | Blob>) => void;
}) {
  // Only the dragging half: a paste is handled once for the whole page, and a
  // row that handled its own would take the same picture twice.
  const { dropping, handlers } = usePhotoDrop((files) => {
    onFiles(index, files);
  });
  const link = findLink(row);
  const said = about(row);
  return (
    <li
      class={`row${active ? " active" : ""}${dropping ? " drop-target" : ""}`}
      onClick={() => {
        onActive(index);
      }}
      // A keyboard reaches the row through the controls in it, and landing on
      // one of those is as much "this row" as tapping it is.
      onFocusCapture={() => {
        onActive(index);
      }}
      onDragOver={handlers.onDragOver}
      onDragLeave={handlers.onDragLeave}
      onDrop={handlers.onDrop}
    >
      <div class="who">
        <input
          type="checkbox"
          aria-label={`Import ${row.line.name}`}
          checked={row.chosen}
          onChange={() => {
            onToggle(index);
          }}
        />
        <div class="about">
          <strong class={row.chosen ? undefined : "off"}>
            {row.line.name}
          </strong>
          {said !== "" && <small>{said}</small>}
        </div>
      </div>
      <ToucanPhotoRow
        photos={row.photos}
        onChange={(photos) => {
          onPhotos(index, photos);
        }}
      >
        <a
          class={`find ${toucanPill}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label}
        </a>
      </ToucanPhotoRow>
      {/* Written on every keystroke rather than on leaving the field: the
          lookup redraws these rows as it settles each one, and a line held
          only on the element would go with the next redraw. */}
      <textarea
        class="note-field"
        rows={2}
        aria-label={`Note about ${row.line.name}`}
        placeholder="What they do, where you met, anything to remember"
        value={row.note}
        onInput={(event) => {
          onNote(index, event.currentTarget.value);
        }}
      />
    </li>
  );
}

/**
 * Bringing a guest list into People. After the paste, the page finds what
 * photographs are free to find. You look at every row, then tag the lot and
 * add them.
 *
 * `activeTagIds` is what the browse list is filtered to, which is nearly
 * always the tag an import started from it belongs on.
 */
export function ImportPage({ activeTagIds }: { activeTagIds: string[] }) {
  const [text, setText] = useState("");
  const [events, setEvents] = useState(2);
  const [rows, setRows] = useState<ImportRow[]>([]);
  // The review replaces the paste rather than sitting under it: what is on the
  // screen is one step or the other, and an empty list is a review of nobody
  // rather than a paste that has not happened.
  const [reviewing, setReviewing] = useState(false);
  const [status, setStatus] = useState("");
  const [left, setLeft] = useState("");
  const [active, setActive] = useState(0);
  const [tagId, setTagId] = useState<string | null>(activeTagIds[0] ?? null);
  const [naming, setNaming] = useState(false);

  // Parsed on every render rather than on a button, so the threshold and the
  // count it lets through answer the paste as it is dropped in.
  const paste = parsePaste(text);
  const threshold = Math.min(events, paste.most);
  const wanted = atLeast(paste.lines, threshold);

  /** Add photos to one row, against whatever the rows are by then. */
  const addPhotos = (index: number, photos: string[]): void => {
    setRows((current) => addRowPhotos(current, index, photos));
  };

  /** Store pictures dropped or pasted onto one row. */
  const addFiles = async (
    index: number,
    files: Iterable<File | Blob>,
  ): Promise<void> => {
    addPhotos(index, (await storePhotos(files)).hashes);
  };

  // A paste has no field to go into, so the page takes it and the row last
  // tapped is where it goes. Only the paste: dragging is per row, which the
  // pointer already aims.
  const pasting = usePhotoDrop((files) => {
    void addFiles(active, files);
  }).handlers.onPaste;

  /** Take the paste into review, and start looking for faces. */
  const review = (): void => {
    const made = rowsFor(store(), wanted);
    setRows(made);
    setReviewing(true);
    setActive(0);
    void findPhotos(
      made,
      (message, remaining) => {
        setStatus(message);
        setLeft(remaining);
      },
      (index, found) => {
        // Against whatever the rows are by then rather than against `made`: a
        // row switched off, or given a picture by hand, while the run was out
        // is not something a lookup landing later may undo.
        setRows((current) =>
          current.map((row, at) =>
            at === index
              ? { ...row, linkedin: found.linkedin, found: found.found }
              : row,
          ),
        );
        addPhotos(index, found.photos);
      },
    );
  };

  if (!reviewing) {
    return (
      <Page className="page-column">
        <p class="note">
          One name per line, or a CSV file. Column <code>name</code> must be
          present. Will also use <code>email</code> to look up photo in
          gravatar, or a Luma <code>user_id</code> to get a photo from a Luma
          profile.
          <code>event_approved_count</code> is a threshold you can filter on.
        </p>
        <textarea
          rows={6}
          aria-label="People to import"
          value={text}
          onInput={(event) => {
            setText(event.currentTarget.value);
          }}
        />
        {paste.most > 1 && (
          <label class="threshold">
            Came to at least {plural(threshold, "event")}
            <input
              type="range"
              aria-label="Events each person came to"
              min={1}
              max={paste.most}
              value={threshold}
              onInput={(event) => {
                setEvents(Number(event.currentTarget.value));
              }}
            />
          </label>
        )}
        <p class="count">
          {String(wanted.length)} of{" "}
          {plural(paste.lines.length, "person", "people")}
        </p>
        <ToucanButton
          variant="primary"
          disabled={wanted.length === 0}
          onClick={review}
        >
          Find photos for {plural(wanted.length, "person", "people")}
        </ToucanButton>
      </Page>
    );
  }

  const chosen = rows.filter((row) => row.chosen);
  const missing = rows.filter(
    (row) => row.chosen && row.photos.length === 0 && row.found !== "",
  );

  return (
    <Page className="page-column" onPaste={pasting}>
      {status !== "" && (
        <div class="status">
          <p class="note">
            {status}
            {left !== "" && <span class="left"> — {left}</span>}
          </p>
          {/* The run notices a stop once the request it is waiting on comes
              back, which is seconds away — so the line says it is stopping
              rather than appearing to ignore the button. */}
          <ToucanButton
            disabled={findingStopped()}
            onClick={() => {
              stopFinding();
              setStatus("Stopping…");
              setLeft("");
            }}
          >
            Stop
          </ToucanButton>
        </div>
      )}
      <p class="note">
        Tap a row to paste a picture onto it, or drop one on it. The switch is
        whether they come in.
      </p>
      <ul class="rows">
        {rows.map((row, index) => (
          <ReviewRow
            key={row.line.name + String(index)}
            row={row}
            index={index}
            active={index === active}
            onActive={setActive}
            onToggle={(at) => {
              setRows(toggleRow(rows, at));
            }}
            onPhotos={(at, photos) => {
              setRows(setRowPhotos(rows, at, photos));
            }}
            onNote={(at, note) => {
              setRows(setRowNote(rows, at, note));
            }}
            onFiles={(at, files) => {
              void addFiles(at, files);
            }}
          />
        ))}
      </ul>
      {missing.length > 0 && (
        <section>
          <h3>Still needs a photograph {missing.length}</h3>
          {/* Above a threshold of one these are people who come back, so who
              is still to be photographed is the useful half of the run. */}
          <ul class="missing">
            {missing.map((row) => (
              <li key={row.line.name} class="note">
                {row.line.name}
              </li>
            ))}
          </ul>
        </section>
      )}
      <ToucanFilePicker
        options={sortedTags(store().tags).map((tag) => ({
          id: tag.id,
          label: tagLabel(tag),
        }))}
        chosen={tagId}
        label="Tag"
        noneLabel="No tag"
        newLabel="＋ New tag"
        onPick={setTagId}
        onNew={() => {
          setNaming(true);
        }}
      />
      <ToucanButton
        variant="primary"
        disabled={chosen.length === 0}
        onClick={() => {
          addChosen(store(), rows, tagId);
          // Nothing to announce, because the list this opens shows the people
          // it just gained.
          go(listPath());
        }}
      >
        Add {plural(chosen.length, "person", "people")}
      </ToucanButton>
      {naming && (
        <ToucanNewThingDialog
          thing="tag"
          placeholder="The event these people came to"
          taken={takenNames(sortedTags(store().tags))}
          onSave={(tagName, emoji) => {
            setTagId(
              addTag(store(), tagName, emoji === "" ? undefined : emoji).id,
            );
          }}
          onClose={() => {
            setNaming(false);
          }}
        />
      )}
    </Page>
  );
}
